/**
 * QUEL ACTE UNE DEMANDE APPELLE-T-ELLE ? — une seule correspondance.
 *
 * ⚠️ CE FICHIER EXISTE PARCE QUE LA CHAÎNE SE ROMPAIT ICI. Mesuré le
 * 13/09/2026 : le guichet ouvre une demande et son dossier, le dossier circule
 * entre services — et AUCUN écran ne savait y rattacher l'acte. `DepotMutation`
 * ne portait même pas le champ, alors que `POST /mutations` l'accepte depuis la
 * migration 053. Conséquence : **13 mutations sur 13 ont `affaire_id` nul**, et
 * les 2 dossiers du registre portent 0 acte. Le parcours administratif d'une
 * donation existait en deux moitiés qui ne se joignaient pas.
 *
 * ⚠️ DEUX VOCABULAIRES EXISTENT DÉJÀ, ON N'EN INVENTE PAS UN TROISIÈME.
 * `fon.demande_type.code` dit `immatriculation_fonciere` ; l'énumération
 * `fon.type_mutation` dit `immatriculation`. Les deux sont légitimes — l'un
 * nomme une DEMANDE d'usager, l'autre un ACTE du registre — mais la traduction
 * doit vivre à UN seul endroit. Recopiée dans un écran, elle divergerait au
 * premier type ajouté, et la divergence se verrait sous sa pire forme : un
 * bouton « déposer l'acte » qui propose le mauvais acte.
 *
 * ⚠️ `null` EST UNE VALEUR, PAS UNE LACUNE. Un extrait cadastral RENSEIGNE, il
 * ne décide rien : aucun acte ne lui correspond, et c'est définitif. Le
 * distinguer d'un type « pas encore traduit » est tout l'objet du `null`
 * explicite — l'écran dit « cette demande n'appelle aucun acte » au lieu de
 * proposer un formulaire vide.
 */
export const ACTE_ATTENDU: Record<string, string | null> = {
  /** Le registre inscrit un immeuble et délivre un titre. */
  immatriculation_fonciere: 'immatriculation',
  /** Transmission à titre gratuit : même mot des deux côtés, et c'est voulu. */
  donation: 'donation',
  /**
   * Transmission à titre onéreux. Même mot des deux côtés, comme la donation.
   *
   * ⚠️ AJOUTÉ LE 20/09/2026 — ET C'EST UNE GARDE EXISTANTE QUI L'A EXIGÉ, pas
   * moi. J'avais inscrit `vente` au catalogue du guichet (migration 155) sans
   * la déclarer ici ; `tests/guichet-acte.test.ts` a rougi au premier passage
   * de la suite complète : « connaît l'acte attendu de CHAQUE type de demande
   * du catalogue ».
   *
   * Sans cette ligne, `acteAttendu('vente')` rendait `undefined` — c'est-à-dire
   * « type inconnu », et non `null` « aucun acte, c'est normal ». L'écran aurait
   * ouvert un dossier de vente sans jamais proposer d'y déposer l'acte : la
   * chaîne se serait rompue exactement là où l'en-tête de ce fichier raconte
   * qu'elle s'était déjà rompue une fois, pour la donation.
   */
  vente: 'vente',
  /**
   * L'extinction d'une sûreté. Même mot des deux côtés, comme la vente.
   *
   * ⚠️ AJOUTÉ LE 21/09/2026, ET C'EST LA MÊME GARDE QUI L'A EXIGÉ, à la même
   * ligne, un jour plus tard. J'avais inscrit `mainlevee` au guichet
   * (migration 172) sans la déclarer ici, et la suite complète a rougi :
   * « connaît l'acte attendu de CHAQUE type de demande du catalogue ».
   *
   * Le commentaire au-dessus racontait déjà l'histoire pour la vente, et
   * au-dessus encore pour la donation. Trois fois le même oubli, trois fois
   * rattrapé par la même garde : c'est elle qui tient la chaîne, pas la
   * vigilance — et c'est la démonstration qu'ajouter un type de mutation
   * touche CINQ couches, dont celle-ci se rappelle toujours en dernier.
   */
  mainlevee: 'mainlevee',
  /**
   * La dévolution successorale. Même mot des deux côtés, comme les trois
   * précédentes — et le code du guichet DOIT être exactement la valeur de
   * `fon.type_mutation`, sans quoi `documents.ts` ne retrouve jamais le
   * formulaire de l'acte (il le cherche par `demande.type_code = mutation.type`).
   *
   * ⚠️ AJOUTÉ LE 21/09/2026 — QUATRIÈME FOIS, MÊME GARDE, MÊME LIGNE. Le
   * commentaire au-dessus dit « trois fois le même oubli » ; en voici une
   * quatrième, commise par quelqu'un qui VENAIT DE LIRE ce fichier pour y
   * chercher autre chose. Ce n'est donc pas un défaut d'attention : c'est que
   * rien, au moment d'écrire la migration, ne rappelle que cette couche existe.
   * La suite complète l'a rattrapée en 91 ms.
   *
   * ⚠️ ET CELA FAIT SIX COUCHES, PAS CINQ, comme l'annonçait la ligne du
   * dessus — recomptées le 21/09 en ajoutant `succession` :
   *   1. `fon.type_mutation` (l'énumération)      4. `doc.acte_autorite` (l'acte)
   *   2. `TYPES_APPLICABLES` (l'effet)            5. **ce fichier** (la traduction)
   *   3. `fon.demande_type` + `FORMULAIRES`       6. `frontend/src/guichet.tsx` (l'écran)
   * La sixième n'était surveillée par rien avant
   * `tests/un-type-au-catalogue-se-remplit.test.ts` : `vente` et `mainlevee`
   * sont restées au catalogue et remplissables nulle part pendant un jour et
   * vingt-quatre heures respectivement.
   */
  succession: 'succession',
  /**
   * L'inscription de sûreté et la radiation — ouvertes au guichet le
   * 22/09/2026 (migration 207).
   *
   * ⚠️⚠️ CINQUIÈME FOIS, MÊME GARDE, MÊME LIGNE. Le commentaire au-dessus en
   * comptait quatre et notait que « rien, au moment d'écrire la migration, ne
   * rappelle que cette couche existe ». Je l'ai lu — et je l'ai quand même
   * oubliée : j'avais recensé QUATRE couches (catalogue, schéma, écran, acte)
   * en croyant les tenir toutes, parce que `tests/un-type-au-catalogue-se-
   * remplit.test.ts` était vert. Il ne surveille pas celle-ci.
   *
   * *Une liste de couches écrite dans un commentaire ne protège personne :
   * c'est la garde qui protège.* Ici, `tests/procedures-donation.test.ts` a
   * arrêté le déploiement avant qu'il ne parte — le déployeur a refusé un
   * rouge NON déclaré, et c'était exactement le bon refus.
   *
   * ⚠️ Le code du guichet est EXACTEMENT la valeur de `fon.type_mutation` pour
   * les deux, comme pour les quatre précédentes : `documents.ts` retrouve le
   * formulaire de l'acte par `demande.type_code = mutation.type`.
   */
  inscription_surete: 'inscription_surete',
  radiation: 'radiation',
  /**
   * L'échange — ouvert au guichet le 22/09/2026 (migration 210), dernier type
   * APPLICABLE qui n'avait aucune porte.
   *
   * ⚠️ UNE DEMANDE, DEUX MUTATIONS. Le contrat est unique et signé par deux
   * copermutants ; son exécution est double, une mutation par fonds. Cette
   * ligne traduit le CODE DU GUICHET vers le type d'acte — elle ne dit rien de
   * la multiplicité, que porte l'instruction.
   */
  echange: 'echange',
  /** Un extrait RENSEIGNE : il n'écrit rien au registre. */
  extrait_cadastral: null,
  /** Un duplicata réédite une pièce ; il ne mute aucun droit. */
  duplicata_titre: null,
};

/**
 * `undefined` pour un type INCONNU — à ne jamais confondre avec `null`, qui
 * dit « aucun acte, et c'est normal ». L'appelant doit traiter les deux
 * séparément : un type au catalogue et absent d'ici est une erreur de
 * configuration, et l'écran doit le DIRE plutôt que de proposer un dépôt au
 * hasard.
 */
export const acteAttendu = (codeDemande: string): string | null | undefined =>
  Object.prototype.hasOwnProperty.call(ACTE_ATTENDU, codeDemande)
    ? ACTE_ATTENDU[codeDemande]
    : undefined;

export const CODES_DEMANDE_CONNUS = Object.keys(ACTE_ATTENDU);
